import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchDocumentConfig,
  replaceDocumentGroups,
  replaceDocumentTypes,
} from "../Dashboard/api";
import type {
  DocumentConfig,
  DocumentGroup,
  DocumentType,
} from "../Dashboard/types";
import { RMS_BOOTSTRAP_QUERY_KEY } from "./bootstrap";

export const DOCUMENT_CONFIG_QUERY_KEYS = {
  all: ["documentConfig"] as const,
  org: (organizationId?: string) => ["documentConfig", organizationId] as const,
};

export const useDocumentConfig = (
  organizationId?: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<DocumentConfig>({
    queryKey: DOCUMENT_CONFIG_QUERY_KEYS.org(organizationId),
    queryFn: () => fetchDocumentConfig(organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled,
  });
};

export const useUpdateDocumentGroups = (organizationId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groups: Omit<DocumentGroup, "id">[]) =>
      replaceDocumentGroups(groups, organizationId),
    // Every read of the configuration, not only this organization's key: in
    // the app the same configuration is read both with the organization
    // named (the types panel) and without (the groups section, the type
    // pickers), and a save through one must refresh the other.
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_CONFIG_QUERY_KEYS.all,
      });
      // The document schema the bootstrap carries offers these as its options.
      queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    },
  });
};

export const useUpdateDocumentTypes = (organizationId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (types: DocumentType[]) =>
      replaceDocumentTypes(types, organizationId),
    // Every read of the configuration, not only this organization's key: in
    // the app the same configuration is read both with the organization
    // named (the types panel) and without (the groups section, the type
    // pickers), and a save through one must refresh the other.
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_CONFIG_QUERY_KEYS.all,
      });
      // The document schema the bootstrap carries offers these as its options.
      queryClient.invalidateQueries({ queryKey: RMS_BOOTSTRAP_QUERY_KEY });
    },
  });
};
